import { useGetHighestAprVault } from '@/hooks/vault/useVault';
import { AppNumber, LARGE_NUMBER } from '@/lib/providers/math/app-number.provider';
import { sofaService } from '@/lib/services/sofa';
import { chainId } from '@/redux/chain.state';
import { LayoutSectionCard } from '@/ui/Components/LayoutSectionCard';
import type { FlexProps } from '@chakra-ui/react';
import {
  Box,
  Button,
  Flex,
  Grid,
  GridItem,
  Image,
  Link,
  Skeleton,
  Text,
  useColorModeValue,
} from '@chakra-ui/react';
import { useQuery } from '@tanstack/react-query';
import { route } from 'nextjs-routes';
import type { FC } from 'react';

type StaticInfoItem = { title: string; value: string; isLoading: boolean };

const StaticInfoItem: FC<StaticInfoItem & FlexProps> = ({ title, value, isLoading, ...props }) => (
  <Flex
    flexDirection="column"
    borderRadius="16px"
    justifyContent="flex-end"
    alignItems="center"
    gap="88px"
    padding="48px 50px 24px 50px;"
    height="240px"
    {...props}
  >
    <Skeleton isLoaded={!isLoading}>
      <Text size="5xl" fontWeight={700}>
        {value}
      </Text>
    </Skeleton>
    <Text size="xl" fontWeight={500}>
      {title}
    </Text>
  </Flex>
);

export const AmplifyVaults: FC = () => {
  const {
    data: tvlValue,
    isFetching,
    isPending,
  } = useQuery({
    queryKey: ['automator_aum_config_tvl_homepage'],
    queryFn: async () => {
      let drvTvl = 0;
      try {
        const aumData = await sofaService.getAutomatorAum();
        drvTvl = Number(aumData.totalDepositAmount) || 0;
      } catch (error) {
        console.warn(error);
      }

      const manualTvl = await fetch(`/daemon/v1/api/${chainId}/vaults/tvl`)
        .then(res => res.json())
        .then(res => res as number);

      return drvTvl + (Number(manualTvl) || 0);
    },
  });

  const highestAprVault = useGetHighestAprVault();
  const isLoading = isFetching || isPending;

  return (
    <LayoutSectionCard>
      <Box
        position="absolute"
        zIndex={1}
        left={0}
        top={0}
        w="full"
        display="flex"
        justifyContent="center"
      >
        <Image
          src={`/images/decor-${useColorModeValue('light', 'dark')}.png`}
          alt="Amplify Vaults"
          objectFit="cover"
          w={{ base: 'full', lg: '976px', xl: '1068px' }}
          h={{ base: '1052px', lg: '976px', xl: '1068px' }}
        />
      </Box>
      <Flex flexDirection="column" position="relative" zIndex={2} w="full" gap="60px">
        <Flex flexDirection="column" gap="28px">
          <Text textAlign="center" size="7xl" as="h1">
            Amplify Vaults
          </Text>
          <Text textAlign="center" size="3xl" w={{ xl: '850px', lg: '810px' }} mx="auto" as="h2">
            Grow your assets through automated strategies across liquid staking, lending, and yield farming.
          </Text>
          <Link href={route({ pathname: '/vaults' })} mx="auto">
            <Button colorScheme="white" size="lg" width="180px">
              Launch App
            </Button>
          </Link>
          <Flex justifyContent="center">
            <Text size="xl">
              build on{' '}
              <Link href="https://sei.io/" target="_blank" textDecor="underline">
                Sei
              </Link>
            </Text>
          </Flex>
        </Flex>
        <Flex
          flexDirection="column"
          position="relative"
          mx="auto"
          width={{ base: 'full', lg: '726px', xl: '850px' }}
        >
          <Grid
            templateColumns={{ base: 'repeat(1, 1fr)', lg: 'repeat(2, 1fr)' }}
            alignItems="center"
            rowGap="20px"
            columnGap="24px"
          >
            <GridItem colSpan={1}>
              <StaticInfoItem
                bgColor={useColorModeValue('#B8D7F8', 'decorative.02')}
                title="Total Value Locked"
                value={`$${AppNumber.from(tvlValue || 0).getDisplayedString(LARGE_NUMBER.kk, 2)}`}
                isLoading={isLoading}
              />
            </GridItem>
            <GridItem colSpan={1}>
              <StaticInfoItem
                bgColor={useColorModeValue('#DFD7F7', 'decorative.04')}
                title="Highest APY"
                value={`${((highestAprVault?.apr?.forwardAPR?.composite?.v3OracleStratRatioAPY ?? 0) * 100).toFixed(2)}%`}
                isLoading={isLoading}
              />
            </GridItem>
          </Grid>
        </Flex>
      </Flex>
    </LayoutSectionCard>
  );
};
